import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Sparkles,
  ArrowLeft,
  ArrowRight,
  Edit3,
  Save,
  RefreshCw,
  Loader2,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  Minus,
  RotateCcw,
} from "lucide-react";
import {
  getProject,
  updateProject,
  updateCard,
  trackPageView,
  trackButtonClick,
} from "@/lib/api";
import axios from "axios";

const baseUrl = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
const API = `${baseUrl}/api`;

const COLOR_PRESETS = [
  "#FF6B6B",
  "#FCD34D",
  "#84A98C",
  "#0EA5E9",
  "#8B5CF6",
  "#EC4899",
  "#F97316",
  "#1F2937",
  "#374151",
  "#6B7280",
  "#9CA3AF",
  "#F3F4F6",
  "#FFFFFF",
  "#FEF3C7",
];

const CARD_DECK_PRICE = 4000;
const MAX_CARD_TEXT_LENGTH = 100; // Max 100 characters

// Helper: Determine if color is light
const isLightColor = (color) => {
  if (!color) return true;
  const hex = color.replace("#", "");
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128;
};

// Card Front component - NO brand name, thinner border, 5px from edge, text always black
function CardFront({
  text,
  cardColor,
  onClick,
  isSmall = false,
  isLarge = false,
}) {
  if (isLarge) {
    return (
      <div
        className="relative rounded-xl bg-white shadow-xl cursor-pointer overflow-hidden w-full h-full"
        onClick={onClick}
        data-testid="card-front"
      >
        <div
          className="absolute rounded"
          style={{
            inset: "4%",
            border: `4.5px solid ${cardColor}`,
            borderRadius: "6px",
            backgroundColor: `${cardColor}1a`,
          }}
        />
        <div className="absolute inset-0 flex items-center justify-center p-[12%]">
          <p
            className="font-playfair font-bold text-center leading-snug"
            style={{
              color: "#1F2937",
              fontSize: "18px",
              hyphens: "auto",
              WebkitHyphens: "auto",
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
          >
            {text}
          </p>
        </div>
      </div>
    );
  }

  // Aspect ratio for 90mm x 60mm = 1.5:1 (width:height), but displayed as portrait
  // So height:width = 1.5:1, meaning height = width * 1.5
  const getFontSize = () => {
    return isSmall ? 9 : 18;
  };
  const width = isSmall ? 90 : 165; // pixels (~18% larger than original 140)
  const height = isSmall ? 135 : 248; // width * 1.5
  const frameOffset = 5; // 5px from edge
  const frameWidth = isSmall ? 3 : 4.5;

  return (
    <div
      className="relative rounded-lg bg-white shadow-lg cursor-pointer overflow-hidden"
      style={{ width: `${width}px`, height: `${height}px` }}
      onClick={onClick}
      data-testid="card-front"
    >
      {/* Frame border with fill */}
      <div
        className="absolute rounded"
        style={{
          top: frameOffset,
          left: frameOffset,
          right: frameOffset,
          bottom: frameOffset,
          border: `${frameWidth}px solid ${cardColor}`,
          borderRadius: "4px",
          backgroundColor: `${cardColor}1a`,
        }}
      />

      {/* Content area - text always black */}
      <div
        className="absolute flex items-center justify-center p-2"
        style={{
          top: frameOffset + frameWidth + 4,
          left: frameOffset + frameWidth + 4,
          right: frameOffset + frameWidth + 4,
          bottom: frameOffset + frameWidth + 4,
        }}
      >
        <p
          className="font-playfair font-bold text-center leading-tight overflow-wrap-anywhere"
          style={{
            color: "#1F2937",
            fontSize: `${getFontSize()}px`, // Itt alkalmazzuk a dinamikus méretet
            hyphens: "auto",
            textWrap: "balance",
            WebkitHyphens: "auto",
            wordBreak: "normal",
            overFlowWrap: "break-word",
            textAlignLast: "center",
          }}
        >
          {text}
        </p>
      </div>
    </div>
  );
}

// Card Back component - solid color with "Kardiy" text
function CardBack({ cardColor, isSmall = false, isLarge = false }) {
  const textColor = isLightColor(cardColor) ? "#1F2937" : "#FFFFFF";

  if (isLarge) {
    return (
      <div
        className="relative rounded-xl shadow-xl overflow-hidden flex items-center justify-center w-full h-full"
        style={{ backgroundColor: cardColor }}
        data-testid="card-back"
      >
        <span
          className="font-heading font-bold"
          style={{ color: textColor, fontSize: "clamp(18px, 5vw, 32px)" }}
        >
          Kardiy
        </span>
      </div>
    );
  }

  const width = isSmall ? 90 : 165;
  const height = isSmall ? 135 : 248;

  return (
    <div
      className="relative rounded-lg shadow-lg overflow-hidden flex items-center justify-center"
      style={{
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: cardColor,
      }}
      data-testid="card-back"
    >
      <span
        className={`font-heading ${isSmall ? "text-base" : "text-lg"} font-bold`}
        style={{ color: textColor }}
      >
        Kardiy
      </span>
    </div>
  );
}

export default function EditorPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [cards, setCards] = useState([]);
  const [originalCards, setOriginalCards] = useState([]);
  const [cardBackColor, setCardBackColor] = useState("#FF5252");
  const [selectionChain, setSelectionChain] = useState(null);
  const [step, setStep] = useState(1); // 1 = color, 2 = cards
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [editText, setEditText] = useState("");
  const [originalEditText, setOriginalEditText] = useState("");
  const [regenerating, setRegenerating] = useState(null);
  const [customColor, setCustomColor] = useState("");

  useEffect(() => {
    trackPageView("editor");
    loadProject();
    const savedCart = localStorage.getItem("kardiy_cart");
    if (savedCart) setCart(JSON.parse(savedCart));
  }, [projectId]);

  const loadProject = async () => {
    try {
      const response = await getProject(projectId);
      const proj = response.data.project;
      setCards(proj.cards || []);
      setOriginalCards(
        proj.cards?.map((c) => ({ id: c.id, front_text: c.front_text })) || [],
      );
      setCardBackColor(proj.card_back_color || "#FF5252");
      setSelectionChain(proj.selection_chain || null);
      if ((proj.cards?.length || 0) >= 50) {
        setCurrentCardIndex(0);
      }
    } catch (error) {
      toast.error("Nem sikerült betölteni");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const previewCardText = useMemo(() => {
    if (cards.length === 0) return "Minta szöveg";
    const longestCard = [...cards].sort(
      (a, b) => (b.front_text?.length || 0) - (a.front_text.length || 0),
    )[0];

    return longestCard?.front_text || "Minta szöveg";
  }, [cards]);

  const handleSaveProject = async (updates = {}) => {
    try {
      await updateProject(projectId, {
        cards,
        card_back_color: cardBackColor,
        ...updates,
      });
    } catch (e) {
      console.error(e);
    }
  };

  const handleNextStep = async () => {
    if (step === 1) {
      await handleSaveProject({ card_back_color: cardBackColor });
      setStep(2);
    }
  };

  const handleEditCard = (card) => {
    setEditingCard(card);
    setEditText(card.front_text);
    setOriginalEditText(card.front_text);
  };

  const handleResetText = () => {
    const original = originalCards.find((c) => c.id === editingCard?.id);
    if (original) {
      setEditText(original.front_text);
    }
  };

  const isTextModified = editingCard && editText !== originalEditText;

  const handleSaveCardText = async () => {
    if (!editingCard) return;
    if (editText.length > MAX_CARD_TEXT_LENGTH) {
      toast.error(`Maximális hossz: ${MAX_CARD_TEXT_LENGTH} karakter`);
      return;
    }
    try {
      await updateCard(projectId, {
        card_id: editingCard.id,
        front_text: editText,
      });
      setCards((prev) =>
        prev.map((c) =>
          c.id === editingCard.id ? { ...c, front_text: editText } : c,
        ),
      );
      setEditingCard(null);
      toast.success("Mentve!");
    } catch (e) {
      toast.error("Hiba");
    }
  };

  const handleRegenerateCard = async (card) => {
    setRegenerating(card.id);
    try {
      const response = await axios.post(
        `${API}/projects/${projectId}/regenerate-card`,
        { card_id: card.id },
      );
      setCards((prev) =>
        prev.map((c) => (c.id === card.id ? response.data.card : c)),
      );
      toast.success("Újragenerálva!");
    } catch (e) {
      toast.error("Hiba");
    } finally {
      setRegenerating(null);
    }
  };

  const handleColorSelect = (color) => {
    setCardBackColor(color);
    setCustomColor(color);
  };

  const handleAddToCart = () => {
    const newItem = {
      id: projectId,
      selectionChain,
      cardBackColor,
      quantity: 1,
    };
    const existingIndex = cart.findIndex((item) => item.id === projectId);
    let updatedCart =
      existingIndex >= 0
        ? cart.map((item, i) =>
            i === existingIndex
              ? { ...item, quantity: item.quantity + 1 }
              : item,
          )
        : [...cart, newItem];
    setCart(updatedCart);
    localStorage.setItem("kardiy_cart", JSON.stringify(updatedCart));
    trackButtonClick("add_to_cart", "editor");
    toast.success("Betéve a kosárba!");
    setShowCart(true);
  };

  const updateCartQuantity = (itemId, delta) => {
    const updatedCart = cart
      .map((item) =>
        item.id === itemId
          ? { ...item, quantity: Math.max(0, item.quantity + delta) }
          : item,
      )
      .filter((item) => item.quantity > 0);
    setCart(updatedCart);
    localStorage.setItem("kardiy_cart", JSON.stringify(updatedCart));
  };

  const removeFromCart = (itemId) => {
    const updatedCart = cart.filter((item) => item.id !== itemId);
    setCart(updatedCart);
    localStorage.setItem("kardiy_cart", JSON.stringify(updatedCart));
  };

  const handleCheckout = () => {
    trackButtonClick("buy_button", "editor");
    localStorage.setItem("kardiy_checkout_decks", JSON.stringify(cart));
    navigate(`/waitlist/${projectId}`);
  };

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = cart.reduce(
    (sum, item) => sum + item.quantity * CARD_DECK_PRICE,
    0,
  );

  if (loading)
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5252]" />
      </div>
    );

  return (
    <>
      <Helmet>
        <title>Kártyaszerkesztő | Kardiy</title>
        <meta
          name="description"
          content="A kártyák szerkesztése és testreszabása a Kardiy-n. Válaszd ki a színt, szerkeszd a szöveget, és add hozzá a kosárhoz egyedi beszélgetésindító kártyáidat!"
        />
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="min-h-screen bg-gradient-to-b from-[#FDFBF7] to-[#FFF1F2] overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-stone-100">
          <div className="max-w-md mx-auto px-4 flex items-center justify-between h-14">
            <button
              onClick={() =>
                step > 1
                  ? setStep(step - 1)
                  : navigate("/create", { state: { returnToStep: 3 } })
              }
              className="p-2 -ml-2 text-stone-500"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#FF5252]" />
              <span className="font-heading font-bold text-[#292524]">
                {step === 1 ? "A pakli formázása" : "Kártyák szerkesztése"}
              </span>
            </div>
            <button
              onClick={() => setShowCart(true)}
              className="relative p-2 -mr-2"
              data-testid="cart-btn"
            >
              <ShoppingCart className="w-5 h-5 text-stone-500" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#FF5252] text-white text-xs rounded-full flex items-center justify-center">
                  {totalItems}
                </span>
              )}
            </button>
          </div>
        </header>

        {/* Progress - 2 steps */}
        <div className="px-4 pt-3 max-w-md mx-auto">
          <div className="flex gap-2">
            {[1, 2, 3, 4, 5].map((s) => (
              <div
                key={s}
                className={`flex-1 h-1 rounded-full ${s <= step + 3 ? "bg-[#FF5252]" : "bg-stone-200"}`}
              />
            ))}
          </div>
        </div>

        {/* Selection Chain Breadcrumb */}
        {selectionChain && (
          <div className="px-4 pt-10 max-w-md mx-auto flex-nowrap overflow-x-auto">
            <div className="flex flex-wrap items-center justify-center gap-1">
              <span className="px-2 py-0.5 bg-[#FF52FF]/10 text-[#292524] rounded-full text-[15px] font-medium">
                {selectionChain.category}
              </span>
              <span className="text-stone-300 text-[15px]">›</span>
              <span className="px-2 py-0.5 bg-[#FF5252]/20 text-[#292524] rounded-full text-[15px] font-medium">
                {selectionChain.level1}
              </span>
              <span className="text-stone-300 text-[15px]">›</span>
              <span className="px-2 py-0.5 bg-[#52FF52]/20 text-[#292524] rounded-full text-[15px] font-medium">
                {selectionChain.level2}
              </span>
              <span className="text-stone-300 text-[15px]">›</span>
              <span className="px-2 py-0.5 bg-[#52FFFF]/10 text-[#292524] rounded-full text-[15px] font-medium">
                {selectionChain.level3}
              </span>
            </div>
          </div>
        )}

        <main className="px-4 py-4 pb-28 max-w-md mx-auto">
          {/* Step 1: Color Selection */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="text-center">
                {/* <h1 className="font-heading text-lg font-bold text-[#292524]">
                Kártya színe
              </h1> */}
                <p className="text-s font-bold text-stone-500">
                  Válaszd ki a kártyák háttérszínét, vagy add meg a saját egyedi
                  színkódodat!
                </p>
              </div>

              {/* Side-by-side Card Preview */}
              <div className="flex justify-center items-center gap-4 py-4">
                <div className="text-center">
                  <CardFront
                    text={previewCardText}
                    cardColor={cardBackColor}
                    onClick={() => {}}
                  />
                  <p className="text-xs text-stone-500 mt-2">Elölnézet</p>
                </div>
                <div className="text-center">
                  <CardBack cardColor={cardBackColor} />
                  <p className="text-xs text-stone-500 mt-2">Hátlap</p>
                </div>
              </div>

              {/* Color Grid */}
              <div className="max-w-[200px] mx-auto">
                <div className="grid grid-cols-7 gap-1.5">
                  {COLOR_PRESETS.map((color) => (
                    <button
                      key={color}
                      onClick={() => handleColorSelect(color)}
                      className={`aspect-square rounded-md border-2 transition-all ${cardBackColor === color ? "border-[#292524] scale-110 shadow-md" : "border-transparent hover:scale-105"}`}
                      style={{ backgroundColor: color }}
                      data-testid={`color-${color.replace("#", "")}`}
                    />
                  ))}
                </div>
              </div>

              {/* Custom color input */}
              <div className="flex gap-2">
                <Input
                  type="color"
                  value={cardBackColor}
                  onChange={(e) => handleColorSelect(e.target.value)}
                  className="w-12 h-10 p-1 rounded-lg cursor-pointer"
                  data-testid="color-picker"
                />
                <Input
                  value={customColor || cardBackColor}
                  onChange={(e) => handleColorSelect(e.target.value)}
                  className="flex-1 rounded-lg text-sm"
                  placeholder="#FF5252"
                  data-testid="color-input"
                />
              </div>
            </div>
          )}

          {/* Step 2: Cards */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center">
                {/* <h1 className="font-heading text-lg font-bold text-[#292524]">
                A kártyáid
              </h1> */}
                <p className="text-s font-bold text-stone-500">
                  Ha valamelyik kártya nem tetszik, egyszerűen szerkeszd meg —
                  vagy generálj egy vadonatújat.
                </p>
              </div>

              {/* Current Card Display */}
              {/* clamp: mobile=65vw (~244px), tablet=360px cap, desktop=360px cap */}
              <div className="flex justify-center py-2">
                {cards[currentCardIndex] && (
                  <div
                    className="relative"
                    style={{
                      width: "clamp(220px, 65vw, 360px)",
                      aspectRatio: "2/3",
                    }}
                  >
                    <CardFront
                      text={cards[currentCardIndex].front_text}
                      cardColor={cardBackColor}
                      onClick={() => handleEditCard(cards[currentCardIndex])}
                      isLarge
                    />
                    {regenerating === cards[currentCardIndex].id && (
                      <div className="absolute inset-0 bg-white/80 rounded-xl flex items-center justify-center">
                        <Loader2 className="w-6 h-6 animate-spin text-[#FF5252]" />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() =>
                    setCurrentCardIndex(Math.max(0, currentCardIndex - 1))
                  }
                  disabled={currentCardIndex === 0}
                  className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center disabled:opacity-30"
                  data-testid="prev-card-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-sm text-stone-500 font-medium">
                  {currentCardIndex + 1} / {cards.length}
                </span>
                <button
                  onClick={() =>
                    setCurrentCardIndex(
                      Math.min(cards.length - 1, currentCardIndex + 1),
                    )
                  }
                  disabled={currentCardIndex === cards.length - 1}
                  className="w-10 h-10 rounded-full bg-white shadow flex items-center justify-center disabled:opacity-30"
                  data-testid="next-card-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>

              {/* Card Actions */}
              <div className="flex justify-center gap-2">
                <button
                  onClick={() => handleEditCard(cards[currentCardIndex])}
                  className="flex items-center gap-1 px-4 py-2 bg-white rounded-full shadow text-sm font-medium"
                  data-testid="edit-card-btn"
                >
                  <Edit3 className="w-4 h-4" />
                  Szerkesztés
                </button>
                <button
                  onClick={() => handleRegenerateCard(cards[currentCardIndex])}
                  className="flex items-center gap-1 px-4 py-2 bg-white rounded-full shadow text-sm font-medium"
                  data-testid="regenerate-card-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  Újra
                </button>
              </div>
            </div>
          )}
        </main>

        {/* Bottom Actions */}
        <div
          className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-stone-100 px-3 pt-3"
          style={{ paddingBottom: "max(16px, env(safe-area-inset-bottom))" }}
        >
          <div className="max-w-md mx-auto">
            {step === 1 ? (
              <Button
                onClick={handleNextStep}
                className="w-full bg-[#FF5252] hover:bg-[#FF6C6C] text-white font-bold py-5 rounded-full"
                data-testid="next-step-btn"
              >
                Tovább
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  onClick={() => navigate("/create")}
                  variant="outline"
                  className="flex-1 rounded-full py-5 text-sm border-[#FF5252] text-[#FF5252] hover:bg-[#FF5252]/10"
                  data-testid="restart-btn"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Újrakezd
                </Button>
                <Button
                  onClick={handleAddToCart}
                  className="flex-1 bg-[#FF5252] hover:bg-[#FF6C6C] text-white font-bold rounded-full py-5 text-sm"
                  data-testid="add-to-cart-btn"
                >
                  <ShoppingCart className="w-4 h-4 mr-1" />
                  Beteszem a kosárba (4000 Ft)
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Cart Dialog */}
        <Dialog open={showCart} onOpenChange={setShowCart}>
          <DialogContent className="max-w-[90vw] mx-4 rounded-2xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader className="pb-2">
              <DialogTitle className="font-heading text-lg flex items-center gap-2">
                <ShoppingCart className="w-4 h-4 text-[#FF5252]" />
                Kosár
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto space-y-2 py-2">
              {cart.length === 0 ? (
                <div className="text-center py-6">
                  <ShoppingCart className="w-10 h-10 text-stone-200 mx-auto mb-2" />
                  <p className="text-stone-500 text-sm">Üres kosár</p>
                </div>
              ) : (
                cart.map((item) => (
                  <div
                    key={item.id}
                    className="bg-stone-50 rounded-xl p-3 cursor-pointer hover:bg-stone-100 transition-colors"
                    onClick={() => {
                      setShowCart(false);
                      navigate(`/editor/${item.id}`);
                    }}
                    data-testid={`cart-item-${item.id}`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Card preview with breadcrumb */}
                      <div className="flex-shrink-0">
                        <div
                          className="w-12 h-18 rounded-lg shadow flex items-center justify-center"
                          style={{ backgroundColor: item.cardBackColor }}
                        >
                          <span
                            className="text-[8px] font-bold font-heading"
                            style={{
                              color: isLightColor(item.cardBackColor)
                                ? "#1F2937"
                                : "#FFFFFF",
                            }}
                          >
                            Kardiy
                          </span>
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm text-[#292524]">
                          {item.selectionChain?.category || "Pakli"}
                        </p>
                        {/* Breadcrumb near the card */}
                        {item.selectionChain && (
                          <div className="flex flex-wrap gap-0.5 mt-1">
                            <span className="text-[8px] text-[#FF6C6C]">
                              {item.selectionChain.category}
                            </span>
                            <span className="text-[8px] text-stone-300">›</span>
                            <span className="text-[8px] text-stone-500">
                              {item.selectionChain.level1}
                            </span>
                            <span className="text-[8px] text-stone-300">›</span>
                            <span className="text-[8px] text-stone-500">
                              {item.selectionChain.level2}
                            </span>
                          </div>
                        )}
                        <p className="text-xs text-stone-500 mt-1">
                          {CARD_DECK_PRICE.toLocaleString()} Ft
                        </p>
                      </div>
                      <div
                        className="flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => updateCartQuantity(item.id, -1)}
                          className="w-7 h-7 rounded-full bg-white shadow flex items-center justify-center"
                          data-testid={`cart-minus-${item.id}`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-5 text-center text-sm">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateCartQuantity(item.id, 1)}
                          className="w-7 h-7 rounded-full bg-white shadow flex items-center justify-center"
                          data-testid={`cart-plus-${item.id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeFromCart(item.id);
                        }}
                        className="p-1 text-stone-400"
                        data-testid={`cart-remove-${item.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            {cart.length > 0 && (
              <div className="border-t border-stone-200 pt-3 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-[#292524]">
                    Összesen ({totalItems})
                  </span>
                  <span className="font-heading text-xl font-bold text-[#FF5252]">
                    {totalPrice.toLocaleString()} Ft
                  </span>
                </div>
                <Button
                  onClick={handleCheckout}
                  className="w-full bg-[#FF5252] hover:bg-[#FF6C6C] text-white font-bold py-5 rounded-full"
                  data-testid="checkout-btn"
                >
                  Megrendelés
                </Button>
                {/* New deck button with border */}
                <Button
                  onClick={() => {
                    setShowCart(false);
                    navigate("/create");
                  }}
                  variant="outline"
                  className="w-full border-[#FF5252] text-[#FF5252] hover:bg-[#FF5252]/10 rounded-full"
                  data-testid="new-deck-btn"
                >
                  + Új pakli készítése
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog
          open={!!editingCard}
          onOpenChange={(o) => !o && setEditingCard(null)}
        >
          <DialogContent className="max-w-[90vw] mx-4 rounded-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">
                Kártya szerkesztése
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              <Textarea
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                rows={3}
                maxLength={MAX_CARD_TEXT_LENGTH}
                className="resize-none rounded-lg text-sm font-medium"
                data-testid="edit-textarea"
              />
              <div className="flex justify-between items-center text-xs">
                <span
                  className={
                    editText.length > MAX_CARD_TEXT_LENGTH * 0.9
                      ? "text-[#FF6C6C]"
                      : "text-stone-400"
                  }
                >
                  {editText.length}/{MAX_CARD_TEXT_LENGTH}
                </span>
                {isTextModified && (
                  <button
                    onClick={handleResetText}
                    className="flex items-center gap-1 text-stone-500 hover:text-[#FF6C6C]"
                    data-testid="reset-text-btn"
                  >
                    <RotateCcw className="w-3 h-3" />
                    Visszaállítás
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setEditingCard(null)}
                className="flex-1 rounded-full w-full border-[#FF5252] text-[#FF5252] hover:bg-[#FF5252]/10 rounded-full"
                // className="flex-1 rounded-full"
                data-testid="cancel-edit-btn"
              >
                Mégse
              </Button>
              <Button
                onClick={handleSaveCardText}
                className="flex-1 bg-[#FF5252] hover:bg-[#FF6C6C] text-white rounded-full"
                data-testid="save-edit-btn"
              >
                <Save className="w-4 h-4 mr-1" />
                Mentés
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
